import { Button, Modal, useToastContext } from '@payaca/ambrosia';
import { readableProjectIntegrationService } from '@payaca/helpers/projectIntegrationsHelper';
import { ProjectIntegrationService } from '@payaca/types/projectIntegrationTypes';
import { FC } from 'react';
import useSyncAllMaterialsFromLinkedEasyPVProject from '../../../../../api/mutations/project/useSyncAllMaterialsFromLinkedEasyPVProject';
import useSyncAllMaterialsFromLinkedHeatpunkProject from '../../../../../api/mutations/project/useSyncAllMaterialsFromLinkedHeatpunkProject';
import useSyncAllMaterialsFromLinkedOpenSolarProject from '../../../../../api/mutations/project/useSyncAllMaterialsFromLinkedOpenSolarProject';

type SyncMaterialsConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
} & SyncMaterialsConfirmationModalContentProps;
const SyncMaterialsConfirmationModal: FC<
  SyncMaterialsConfirmationModalProps
> = (props) => {
  const { isOpen, service, ...rest } = props;
  return (
    <Modal
      title={`Sync Materials from ${
        service ? readableProjectIntegrationService[service] : ''
      }`}
      isOpen={isOpen}
      {...rest}
    >
      <SyncMaterialsConfirmationModalContent service={service} {...rest} />
    </Modal>
  );
};

type SyncMaterialsConfirmationModalContentProps = {
  onClose?: () => void;
  service?: ProjectIntegrationService;
  projectId: string;
  projectIntegrationId?: string;
};
const SyncMaterialsConfirmationModalContent: FC<
  SyncMaterialsConfirmationModalContentProps
> = (props) => {
  const { onClose, service, projectId, projectIntegrationId } = props;
  const { pushInfo } = useToastContext();

  const {
    mutate: syncAllMaterialsFromLinkedEasyPVProject,
    isPending: isEasyPVPending,
  } = useSyncAllMaterialsFromLinkedEasyPVProject({
    projectId,
    options: {
      onSuccess: () => {
        onClose?.();
        pushInfo(
          `Material sync started - we will notify you when it's complete.`
        );
      },
    },
  });
  const {
    mutate: syncAllMaterialsFromLinkedHeatpunkProject,
    isPending: isHeatpunkPending,
  } = useSyncAllMaterialsFromLinkedHeatpunkProject({
    projectId,
    options: {
      onSuccess: () => {
        onClose?.();
        pushInfo(
          `Material sync started - we will notify you when it's complete.`
        );
      },
    },
  });
  const {
    mutate: syncAllMaterialsFromLinkedOpenSolarProject,
    isPending: isOpenSolarPending,
  } = useSyncAllMaterialsFromLinkedOpenSolarProject({
    projectId,
    options: {
      onSuccess: () => {
        onClose?.();
        pushInfo(
          `Material sync started - we will notify you when it's complete.`
        );
      },
    },
  });

  const isPending = isEasyPVPending || isHeatpunkPending || isOpenSolarPending;

  return (
    <>
      <Modal.Body>
        <p>
          {`Are you sure you want to sync Materials to this Project? This will update any
            Materials that were previously synced from ${
              service ? readableProjectIntegrationService[service] : ''
            } and overwrite any manually updated quantities for these Materials.`}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button onClick={onClose} variant="white">
            Cancel
          </Button>
          <Button
            disabled={isPending}
            onClick={() => {
              if (!projectIntegrationId) return;
              if (service === 'EASY_PV') {
                syncAllMaterialsFromLinkedEasyPVProject({
                  publicId: projectIntegrationId,
                  projectId,
                });
              } else if (service === 'HEATPUNK') {
                syncAllMaterialsFromLinkedHeatpunkProject({
                  publicId: projectIntegrationId,
                  projectId,
                });
              } else if (service === 'OPEN_SOLAR') {
                syncAllMaterialsFromLinkedOpenSolarProject({
                  publicId: projectIntegrationId,
                  projectId,
                });
              }
            }}
          >
            Sync
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </>
  );
};

export default SyncMaterialsConfirmationModal;
